import React from "react";
import CustomSnackbar from "../../../components/Feedback/CustomSnackbar";
import {
  selectCleaningZonesRestSet,
  selectRedZonesRestSet,
  selectRuleZonesRestSet,
  selectGatesRestSet,
  selectLandmarksRestSet,
  selectLimitZonesRestSet,
  selectWaypointsRestSet,
} from "../../../store/room/zone-planner/zone-planner-selectors";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { useTranslation } from "react-i18next";
import { errorResetResponse, isErrorSetOpen, isSuccessSetOpen, successResetResponse } from "../../../interfaces/api/rest-set";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import { selectNavTasksData, selectRobotRelayGateApiConfigs } from "../../../store/robot-configurations/robot-configurations-selectors";
import { selectIsWebApp } from "../../../store/app-configurations/app-configurations-selectors";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";

const ZonePlannerSnackBars: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const {
    patchCleaningZonesRestSet,
    patchGatesRestSet,
    patchLandmarksRestSet,
    patchLimitZonesRestSet,
    patchRedZonesRestSet,
    patchRuleZonesRestSet,
    patchWaypointsRestSet,
    toggleWaypointsEnabledAutomatically,
    resetRelayGateConfigPutStatus,
  } = useSharedActions();

  const cleaningZonesRestSet = useSharedSelector((state) => selectCleaningZonesRestSet(state));
  const redZonesRestSet = useSharedSelector((state) => selectRedZonesRestSet(state));
  const ruleZonesRestSet = useSharedSelector((state) => selectRuleZonesRestSet(state));
  const gatesRestSet = useSharedSelector((state) => selectGatesRestSet(state));
  const landmarksRestSet = useSharedSelector((state) => selectLandmarksRestSet(state));
  const limitZonesRestSet = useSharedSelector((state) => selectLimitZonesRestSet(state));
  const waypointsRestSet = useSharedSelector((state) => selectWaypointsRestSet(state));
  const relayGateConfigPutStatus = useSharedSelector((state) => selectRobotRelayGateApiConfigs(state)).putStatus;
  const waypointsEnabledAutomatically = useSharedSelector((state) => selectNavTasksData(state)).waypointsEnabledAutomatically;
  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));

  const resetSuccessRestHandler = () => {
    dispatch(patchCleaningZonesRestSet(successResetResponse(cleaningZonesRestSet)));
    dispatch(patchGatesRestSet(successResetResponse(gatesRestSet)));
    dispatch(patchLandmarksRestSet(successResetResponse(landmarksRestSet)));
    dispatch(patchLimitZonesRestSet(successResetResponse(limitZonesRestSet)));
    dispatch(patchRedZonesRestSet(successResetResponse(redZonesRestSet)));
    dispatch(patchRuleZonesRestSet(successResetResponse(ruleZonesRestSet)));
    dispatch(patchWaypointsRestSet(successResetResponse(waypointsRestSet)));
    if(isWebapp){
      dispatch(resetRelayGateConfigPutStatus());
    }
  };

  const resetErrorRestHandler = () => {
    dispatch(patchCleaningZonesRestSet(errorResetResponse(cleaningZonesRestSet)));
    dispatch(patchGatesRestSet(errorResetResponse(gatesRestSet)));
    dispatch(patchLandmarksRestSet(errorResetResponse(landmarksRestSet)));
    dispatch(patchLimitZonesRestSet(errorResetResponse(limitZonesRestSet)));
    dispatch(patchRedZonesRestSet(errorResetResponse(redZonesRestSet)));
    dispatch(patchRuleZonesRestSet(errorResetResponse(ruleZonesRestSet)));
    dispatch(patchWaypointsRestSet(errorResetResponse(waypointsRestSet)));
    if(isWebapp){
      dispatch(resetRelayGateConfigPutStatus());
    }
  };

  const errorMessages = [
    ...(isErrorSetOpen(cleaningZonesRestSet) ? getSaveErrorMessages(cleaningZonesRestSet.deleteError ?? cleaningZonesRestSet.patchError ?? cleaningZonesRestSet.postError) : []),
    ...(isErrorSetOpen(redZonesRestSet) ? getSaveErrorMessages(redZonesRestSet.deleteError ?? redZonesRestSet.patchError ?? redZonesRestSet.postError) : []),
    ...(isErrorSetOpen(ruleZonesRestSet) ? getSaveErrorMessages(ruleZonesRestSet.deleteError ?? ruleZonesRestSet.patchError ?? ruleZonesRestSet.postError) : []),
    ...(isErrorSetOpen(limitZonesRestSet) ? getSaveErrorMessages(limitZonesRestSet.deleteError ?? limitZonesRestSet.patchError ?? limitZonesRestSet.postError) : []),
    ...(isErrorSetOpen(gatesRestSet) ? getSaveErrorMessages(gatesRestSet.deleteError ?? gatesRestSet.patchError ?? gatesRestSet.postError) : []),
    ...(isErrorSetOpen(landmarksRestSet) ? getSaveErrorMessages(landmarksRestSet.deleteError ?? landmarksRestSet.patchError ?? landmarksRestSet.postError) : []),
    ...(isErrorSetOpen(waypointsRestSet) ? getSaveErrorMessages(waypointsRestSet.deleteError ?? waypointsRestSet.patchError ?? waypointsRestSet.postError) : []),
    ...(isWebapp && relayGateConfigPutStatus.status === HttpFetchStatus.Failed ? getSaveErrorMessages(relayGateConfigPutStatus.error) : []),
  ]
  const uniqueErrorMessages = [...new Set(errorMessages)];

  return (
    <React.Fragment>
      <CustomSnackbar
        open={
          isSuccessSetOpen(cleaningZonesRestSet) ||
          isSuccessSetOpen(redZonesRestSet) ||
          isSuccessSetOpen(ruleZonesRestSet) ||
          isSuccessSetOpen(limitZonesRestSet) ||
          isSuccessSetOpen(gatesRestSet) ||
          isSuccessSetOpen(landmarksRestSet) ||
          isSuccessSetOpen(waypointsRestSet) ||
          (isWebapp && relayGateConfigPutStatus.status === HttpFetchStatus.Success)
        }
        title={t("success-saving-map-planner")}
        textMessages={[]}
        onClose={resetSuccessRestHandler}
      />
      <CustomSnackbar
        open={
          isErrorSetOpen(cleaningZonesRestSet) ||
          isErrorSetOpen(redZonesRestSet) ||
          isErrorSetOpen(ruleZonesRestSet) ||
          isErrorSetOpen(limitZonesRestSet) ||
          isErrorSetOpen(gatesRestSet) ||
          isErrorSetOpen(landmarksRestSet) ||
          isErrorSetOpen(waypointsRestSet) ||
          (isWebapp && relayGateConfigPutStatus.status === HttpFetchStatus.Failed)
        }
        title={t("error-saving-map-planner")}
        textMessages={uniqueErrorMessages}
        onClose={resetErrorRestHandler}
        severity="error"
      />
       <CustomSnackbar
          title={t("waypoints-enabled-automatically")}
          open={waypointsEnabledAutomatically}
          onClose={() => dispatch(toggleWaypointsEnabledAutomatically())}
          severity="info"
      />
    </React.Fragment>
  );
};

export default ZonePlannerSnackBars;
